import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === undefined) return null;
  if (user) return <Navigate to={location.state?.from?.pathname || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-neutral-950 text-white p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm bg-blue-600 flex items-center justify-center">
            <span className="text-white font-display font-extrabold">W</span>
          </div>
          <span className="font-display font-extrabold tracking-tight text-lg">Wydely</span>
        </div>
        <div>
          <h1 className="font-display text-5xl xl:text-6xl font-extrabold tracking-tighter leading-[0.95]">
            Internal
            <br />
            Admin
            <br />
            Console<span className="text-blue-500">.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-sm text-sm leading-relaxed">
            Business health, WhatsApp delivery analytics, template approvals and billing — in one dense, ops-grade view.
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          Restricted access · Ops team only
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your admin credentials to continue.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@wydely.io"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
